import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fetchData } from "../../services/BackendServices";

// Single SVG icons for each tab
const tabIcons = {
  legal: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  ),
  ownership: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  trade: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  compliance: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <path d="M9 15l2 2 4-4"></path>
    </svg>
  ),
  operational: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="2" y1="12" x2="22" y2="12"></line>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
  ),
  banking: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  ),
  assessments: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
};

const categories = [
  { key: "legal", label: "Legal Identity" },
  { key: "ownership", label: "Ownership" },
  { key: "trade", label: "Trade History" },
  { key: "compliance", label: "Compliance" },
  { key: "operational", label: "Operational" },
  { key: "banking", label: "Banking" },
  { key: "assessments", label: "Assessments" },
];

// Updated field mapping - Registered Address moved to Operational tab
const tabFields = {
  legal: [
    { label: "Legal Business Name", field: "LegalBusinessName" },
    { label: "Business Type", field: "BusinessType" },
    { label: "Reg / Inc Number", field: "CompanyRegistrationNumber" },
    { label: "Country of Inc", field: "CountryOfIncorporation" },
    { label: "Year of Incorporation", field: "DateOfIncorporation" },
  ],
  ownership: [
    { label: "Promoter / Founder Name", field: "PromoterName" },
    { label: "Ownership Disclosed?", field: "OwnershipDisclosed" },
    { label: "% Shareholding Known", field: "Shareholding" },
    { label: "Mgmt Experience (Years)", field: "MgmtExperience" },
    { label: "Key Management Names", field: "DirectorNames" },
  ],
  trade: [
    { label: "Years in Commodity Trading", field: "TradingYears" },
    { label: "Annual Trade Volume Range", field: "TradeVolume" },
    { label: "Export/Import Experience?", field: "EximExperience" },
    { label: "Main Commodities Traded", field: "Commodities" },
    { label: "Countries Traded With", field: "CountriesTraded" },
  ],
  compliance: [
    { label: "AML Policy Declared", field: "AmlPolicy" },
    { label: "KYC Documents Submitted", field: "KycSubmitted" },
    { label: "Sanctions Compliance Declaration", field: "SanctionsCheck" },
    { label: "Litigation Disclosure", field: "Litigation" },
  ],
  operational: [
    { label: "Company Website URL", field: "Website" },
    { label: "Official Email Domain", field: "EmailID" },
    { label: "Years Website Active", field: "WebsiteAge" },
    { label: "Business Phone Number", field: "ContactNumber" },
    { label: "Registered Address", field: "RegisteredAddress" },
    { label: "Operational Address", field: "OperationalAddress" },
  ],
  banking: [
    { label: "Primary Bank Name", field: "BankName" },
    { label: "Account Type", field: "AccountType" },
    { label: "Years with Bank", field: "BankYears" },
    { label: "LC / BG Usage History?", field: "LcUsage" },
  ],
};

const TrustScoreModal = ({ onClose, userEmail }) => {
  const [activeTab, setActiveTab] = useState("legal");
  const [isLoading, setIsLoading] = useState(true);
  const [kybData, setKybData] = useState(null);
  const [trustScore, setTrustScore] = useState(0);
  const [trustLevel, setTrustLevel] = useState("");
  const [scoreDescription, setScoreDescription] = useState("");
  const [feedbackItems, setFeedbackItems] = useState([]);
  const animationRef = useRef(null);

  const fetchKYBData = useCallback(async (email) => {
    try {
      setIsLoading(true);
      const response = await fetchData({
        target: "listUserKYB",
        userOrder: "UserKybID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: email },
      });
      if (response.items && response.items.length > 0) {
        setKybData(response.items[0]);
      } else {
        setKybData({});
      }
    } catch (err) {
      console.error(err);
      setKybData({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userEmail) fetchKYBData(userEmail);
  }, [userEmail, fetchKYBData]);

  const hasValue = (field) => {
    if (!kybData || !field) return false;
    const val = kybData[field];
    return !(val === null || val === undefined || val === "" || val === 0);
  };

  const isApproved = kybData?.Status === "Approved";

  // Trust Score Calculation
  const calculateTrustScore = useCallback(() => {
    if (!kybData || isLoading) return;

    let score = 0;
    const checks = [];

    const incYear = kybData.DateOfIncorporation ? new Date(kybData.DateOfIncorporation).getFullYear() : 2025;
    const age = 2025 - incYear;
    if (age > 10) {
      score += 10;
      checks.push({ type: "success", text: "Established Business (>10 yrs)" });
    } else if (age > 5) {
      score += 7;
      checks.push({ type: "success", text: "Stable Operation (>5 yrs)" });
    } else if (age > 2) {
      score += 4;
    } else {
      checks.push({ type: "warning", text: "Young Entity (<2 yrs)" });
    }

    const structure = (kybData.BusinessType || "").toLowerCase();
    if (structure.includes("public")) score += 10;
    else if (structure.includes("private") || structure.includes("ltd")) score += 8;
    else score += 5;

    if (hasValue("LegalBusinessName")) score += 5;
    if (hasValue("CompanyRegistrationNumber")) score += 5;

    if (hasValue("Website")) {
      score += 6;
      checks.push({ type: "success", text: "Established Digital Presence" });
    }
    if (hasValue("EmailID") && !kybData.EmailID.toLowerCase().includes("gmail") && !kybData.EmailID.toLowerCase().includes("yahoo")) {
      score += 4;
      checks.push({ type: "success", text: "Corporate Email Domain" });
    }

    if (hasValue("BankName")) {
      score += 8;
      checks.push({ type: "success", text: "Long-term Banking Relationship" });
    }

    if (isApproved) {
      score += 25;
      checks.push({ type: "success", text: "Fully Verified & Approved" });
    } else {
      checks.push({ type: "warning", text: "Verification Pending" });
    }

    score = Math.min(100, Math.max(0, Math.round(score)));

    let level = "No Trust";
    let desc = "This entity poses a significant risk.";
    if (score >= 80) {
      level = "Verified Trust";
      desc = "This entity shows strong signs of reliability and long-term stability.";
    } else if (score >= 60) {
      level = "Moderate Trust";
      desc = "This entity is operational but may lack long-term history or full transparency.";
    } else if (score >= 40) {
      level = "Caution";
      desc = "Exercise due diligence. Some risk factors were identified.";
    }

    setTrustScore(score);
    setTrustLevel(level);
    setScoreDescription(desc);
    setFeedbackItems(checks);
  }, [kybData, isLoading]);

  useEffect(() => {
    if (!isLoading && kybData) calculateTrustScore();
  }, [isLoading, kybData, calculateTrustScore]);

  // Score animation
  useEffect(() => {
    if (activeTab === "assessments" && trustScore > 0) {
      if (animationRef.current) clearInterval(animationRef.current);
      let current = 0;
      animationRef.current = setInterval(() => {
        current += Math.ceil(trustScore / 40);
        if (current >= trustScore) {
          current = trustScore;
          clearInterval(animationRef.current);
        }
        const el = document.getElementById("animated-score");
        if (el) el.textContent = current;
      }, 30);
    }
    return () => {
      if (animationRef.current) clearInterval(animationRef.current);
    };
  }, [activeTab, trustScore]);

  const ErrorIndicator = () => (
    <div style={{
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      backgroundColor: "#ef4444",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
      <CloseIcon sx={{ color: "#fff", fontSize: 18 }} />
    </div>
  );

  const renderFields = () => {
    if (isLoading) {
      return <div style={{ textAlign: "center", padding: "40px", fontSize: "18px", color: "#64748b" }}>Loading...</div>;
    }

    if (activeTab === "assessments") {
      const getColor = () => {
        if (trustScore >= 80) return "#10b981";
        if (trustScore >= 60) return "#2563eb";
        if (trustScore >= 40) return "#f59e0b";
        return "#ef4444";
      };

      return (
        <div style={{ padding: "20px 30px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "30px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", width: "140px", height: "140px" }}>
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="60" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke={getColor()}
                  strokeWidth="10"
                  strokeDasharray={`${(trustScore / 100) * 377} 377`}
                  transform="rotate(-90 70 70)"
                />
              </svg>
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <div id="animated-score" style={{ fontSize: "36px", fontWeight: 800, color: getColor() }}>0</div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>/ 100</div>
              </div>
            </div>
            <div style={{ textAlign: "left" }}>
              <h3 style={{ fontSize: "24px", fontWeight: 700, color: getColor(), margin: "0 0 8px 0" }}>{trustLevel}</h3>
              <p style={{ fontSize: "14px", color: "#4b5563", maxWidth: "360px", lineHeight: "1.4" }}>{scoreDescription}</p>
            </div>
          </div>

          <div style={{ marginTop: "24px", maxWidth: "600px", margin: "0 auto" }}>
            {feedbackItems.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", borderBottom: i < feedbackItems.length - 1 ? "1px solid #e2e8f0" : "none" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={item.type === "success" ? "#10b981" : "#f59e0b"} strokeWidth="2">
                  {item.type === "success" ? (
                    <polyline points="20 6 9 17 4 12" />
                  ) : (
                    <>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </>
                  )}
                </svg>
                <span style={{ fontSize: "14px", color: "#374151" }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    const fields = tabFields[activeTab] || [];

    return (
      <div style={{ padding: "8px 24px", display: "flex", flexDirection: "column", gap: "2px" }}>
        {fields.map(({ label, field }) => {
          const available = hasValue(field);

          return (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#ffffff",
                padding: "10px 16px",
                borderRadius: "6px",
                minHeight: "44px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ fontSize: "13px", color: "#4b5563", fontWeight: 500 }}>{label}</span>
              <div style={{ width: "36px", display: "flex", justifyContent: "center" }}>
                {available ? (
                  <Checkbox
                    checked
                    disabled
                    size="medium"
                    sx={{
                      color: "#10b981",
                      "&.Mui-checked": { color: "#10b981" },
                      padding: 0,
                    }}
                  />
                ) : (
                  <ErrorIndicator />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getStatusBadge = () => {
    const approved = kybData?.Status === "Approved";
    return (
      <Box sx={{
        backgroundColor: approved ? "#d1fae5" : "#fef3c7",
        color: approved ? "#065f46" : "#b45309",
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "0.75rem",
        fontWeight: 600,
        display: "inline-block",
      }}>
        {approved ? "Verified" : "Unverified"}
      </Box>
    );
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 999999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        width: "900px",
        maxWidth: "96%",
        height: "92vh",
        maxHeight: "920px",
        background: "#fff",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          padding: "12px 24px",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>
          Verification Status
          </h3>
          <button style={{ background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: "#64748b" }} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Status */}
        {!isLoading && (
          <div
          className="text-header"
          style={{
            padding: "10px 30px", // Match padding with tab content
            fontSize: "16px",
            color: "#575555ff",
            fontWeight: "500",
          }}
        >
          Seller: {getStatusBadge()}
        </div>
        )}

        {/* Tabs */}
        <div style={{ backgroundColor: "#f8fafc", padding: "12px 0", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
          <div style={{ display: "flex", overflowX: "auto", padding: "0 16px", gap: "20px" }}>
            {categories.map((cat) => (
              <div
                key={cat.key}
                onClick={() => setActiveTab(cat.key)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  padding: "6px",
                  minWidth: "90px",
                }}
              >
                <div style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  backgroundColor: activeTab === cat.key ? "#ea580c" : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: activeTab === cat.key ? "0 4px 10px rgba(234, 88, 12, 0.3)" : "0 1px 4px rgba(0,0,0,0.1)",
                }}>
                  <div style={{ color: activeTab === cat.key ? "#fff" : "#ea580c" }}>
                    {tabIcons[cat.key]}
                  </div>
                </div>
                <span style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: activeTab === cat.key ? "#ea580c" : "#4b5563",
                  textAlign: "center",
                }}>
                  {cat.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div style={{ flex: 1, overflowY: "auto", backgroundColor: "#fff" }}>
          {renderFields()}
        </div>

        {/* Footer Legend */}
        <div style={{
          padding: "12px 24px",
          backgroundColor: "#f8fafc",
          display: "flex",
          justifyContent: "center",
          gap: "32px",
          flexWrap: "wrap",
          fontSize: "13px",
          flexShrink: 0,
          borderTop: "1px solid #e2e8f0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ErrorIndicator />
            <span>Not Available</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox checked disabled size="medium" sx={{ color: "#9ca3af", "&.Mui-checked": { color: "#9ca3af" }, padding: 0 }} />
            <span>Submitted (Pending)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox checked disabled size="medium" sx={{ color: "#10b981", "&.Mui-checked": { color: "#10b981" }, padding: 0 }} />
            <span>Approved</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustScoreModal;